import { publicAuthenticatorRequest, privateLoggerRequest, privateAuthenticatorRequest, privatePaymentServerRequest, makePrivateRequestAsManager } from "./managers/requestBuilder"
import {
    getAccountType,
    getDistributorId,
    getInstallerId,
    getManagementToken,
    getRegion,
    getUid
} from "./managers/store";

import { getDisplayedCloudDays } from "../utils/plan";

/**
 * Returns cameras belonging to specified user.
 * @param {number} uid user uid
 * @param {string} installerId id of user's installer
 * @param {string} region Region of auth to use.
 * @returns object mapping device id to object of camera info
 */
export const getUserCameras = async (uid, installerId, region, allInfo = false, includeDeleted = false) => {
    const { data: { result, err } } = await publicAuthenticatorRequest('GET', `/users/${uid}/devices`, {
        params: {
            token: getManagementToken(),
            manageruid: getUid(),
            installerid: installerId,
            managementType: 's',
            managementScope: 'all'
        }
    }, region);

    // API returns 200 if an error occurs
    if (err) {
        throw new Error(err);
    }

    return result.reduce(
        (acc, { is_camera, id, adaptr_cloud_id, phonename, logger, ...camera }) => {
            if (includeDeleted || is_camera ) {
                return { ...acc, [id]: { name: phonename, cloudAdapterId: adaptr_cloud_id, logger,...(allInfo ? {...camera, is_camera } : {}) }};
            } else {
                return acc;
            }
        }, {});
}

/**
 * Returns raw devices for a managed customer (installer/distributor manager).
 * Mirrors the management portal /users/:uid/devices request.
 * @param {number} uid user uid
 * @param {string} [regionOverride] Optional auth region override.
 * @returns {Promise<object[]>} Array of device records.
 */
export const getInstallerCustomerDevices = async (uid, regionOverride) => {
    const managementType = getAccountType();
    const installerId = getInstallerId();
    const distributorId = getDistributorId();
    const managementScope =
        managementType === 'i'
            ? installerId
            : managementType === 'd'
            ? distributorId
            : 'all';

    const token = getManagementToken();

    if (!token || !managementType) {
        console.warn('getInstallerCustomerDevices: missing management credentials', {
            hasToken: !!token,
            managementType
        });
    }

    const { data: { result, err } } = await publicAuthenticatorRequest(
        'GET',
        `/users/${uid}/devices`,
        {
            params: {
                token,
                manageruid: getUid(),
                installerid: installerId,
                distributorid: distributorId,
                managementType,
                managementScope
            }
        },
        regionOverride || getRegion()
    );

    // API returns 200 if an error occurs
    if (err) {
        throw new Error(err);
    }

    return result ?? [];
};

/**
 * Send a camera task as a staff member.
 * @param {string} logger Camera logger.
 * @param {string} uidd Camera uidd.
 * @param {('healthcheck', 'logupload', 'forceupdate', 'restartapp')} action Action to send.
 * @returns message returned ("request sent to camera")
 */
export const staffSendCameraTask = async (logger, uidd, action) => {
    const { data } = await privateLoggerRequest(
        logger,
        'GET',
        '/sendcameratask',
        {
            params: {
                uid: uidd,
                action,
            },
        },
        true
    );

    if (data.err) {
        throw new Error(data.err);
    }

    return data.result;
};

/**
 * @typedef AdapterInfo
 * @property {string} cloudAdapterId
 * @property {string} region
 */

/**
 * Returns all adapters belonging to a user.
 * 
 * @param {number} uid 
 * @returns {object<string,AdapterInfo>} Object mapping Cloud Adapter ID to info.
 */
export const getUserAdapters = async (uid) => {
    const { data } = await privateAuthenticatorRequest(
        'GET',
        `/adapters/${uid}`,
        undefined,
        true
    );

    if (!data) {
        return {};
    }

    return data.reduce((acc, { cloudid, region, site }) => ({ ...acc, [cloudid]: { cloudId: cloudid, region, site }}), {});
};

export const forceAdapterVersion = makePrivateRequestAsManager(async ({ makePrivateRequest }, cloudId, updates) => {
    const res = await makePrivateRequest(
        'auth',
        'PUT',
        `/support/adapter/${cloudId}/config`,
        {
            data: updates
        }
    );
    return res;
});

/**
 * Returns the shadow for the specified Adapter.
 * 
 * @param {string} cloudId Cloud Adapter Id.
 * @param {string} [shadowName] Optional shadow name to fetch a child shadow.
 * @returns {Promise<object>} Shadow object.
 */
export const getAdapterShadow = makePrivateRequestAsManager(async ({ makePrivateRequest }, cloudId, name) => {
    const { data } = await makePrivateRequest(
        'auth',
        'GET',
        `/adapter/${cloudId}/shadow${name ? `/${name}` : ''}`
    );

    return data;
});

export const getUserDevices = makePrivateRequestAsManager(async ({ makePrivateRequest }, uid) => {
    const { data } = await makePrivateRequest(
        'auth',
        'GET',
        `/support/devices/${uid}`
    );

    return data;
});
export const patchCameraSettings = async (userId, deviceId, settings) => {
    try {
        await privateAuthenticatorRequest(
            'PATCH',
            `/camera/${userId}/${deviceId}/config`,
            {
                data: settings
            },
            true,
            true
        );
    } catch (err) {
        throw err;
    }
};


/**
 * Search for a user by uid, email or cloud ID.
 * 
 * If searching for a uid or Cloud ID, will either return object containing user info or throw 404 error.
 * 
 * If searching by email, will return array of results. Given string does not have to be whole email; the API
 * will return partial matches.
 * 
 * @throws 404 if searching by uid or cloud ID and no matching user could be found.
 * 
 * @param {('uid', 'cloudId', 'email')} type Search type.
 * @param {number|string} searchValue Uid, cloud ID or email to search for.
 * @returns {object|object[]}
 */
export const searchUsers = async (type, searchValue) => {
    const params = {};
    switch (type) {
        case 'uid':
            params.uid = searchValue;
            break;
        case 'cloudId':
            params.cloudAdapterId = searchValue;
            break;
        case 'partialCloudId':
            params.partialCloudAdapterId = searchValue;
            break;
        case 'email':
        default:
            params.email = searchValue;
            break;
    }

    const { data } = await privateAuthenticatorRequest(
        'GET',
        '/support/search/user',
        {
            params
        },
        true
    );

    return data;
}

/**
 * Returns payment link for an EUP end user.
 * @param {number} uid EUP end user's uid.
 * @param {string} region EUP end user's region.
 * @returns {string} Payment link.
 */
export const generateEndUserPaymentLink = async (uid, region) => {
    const { data } = await publicAuthenticatorRequest(
        'POST',
        `/paymentlink/user/${uid}`,
        {
            headers: {
                Authorization: `ManythingToken ${getManagementToken()}`
            }
        },
        region
    );

    return data;
}

/**
 * Returns payment link for an invoice installer to set up autopay.
 * @param {string} installerId Invoice installer id.
 * @returns {string} Payment link.
 */
export const generateInstallerAutopayPaymentLink = async (installerId) => {
    const { data } = await privateAuthenticatorRequest(
        'POST',
        `/paymentlink/installer`,
        {
            data: {
                manageruid: getUid(),
                installerid: installerId
            }
        },
        true,
        true
    );

    return data;
}

/**
 * Deletes an invoice item. Only works for invoice items that have not been added to an invoice yet.
 * @param {number} invoiceItemId Id of item.
 * @param {string} installerId Id of installer who is being invoiced for the item.
 * @returns Axios promise.
 */
export const deleteInvoiceItem = async (invoiceItemId, installerId) => {
    return privatePaymentServerRequest(
        'DELETE',
        `/invoiceitems/${invoiceItemId}`,
        {
            data: {
                installerId
            }
        },
        true,
        true
    );
}

/**
 * Get account change logs for a uid.
 * @param uid
 * @returns {object[]}
 */
export const getAccountChangeLogs = makePrivateRequestAsManager(async ({ makePrivateRequest }, uid) => {
    const { data: logs } = await makePrivateRequest(
        'payment',
        'GET',
        '/account/:uid/plan/changes'
    );

    return logs.map(
        ({
            time,
            previous_accountlevel,
            previous_cloud_days,
            accountlevel,
            cloud_days,
            previous_maxdevices,
            maxdevices,
            previous_devices_quota,
            devices_quota,
            previous_analytics_devices,
            analytics_devices,
            account_change_date,
            previous_sub_renewal_frequency_months,
            sub_renewal_frequency_months,
            ...log
        }) => {
            return {
                time: new Date(time),
                previousAccountLevel: previous_accountlevel,
                previousCloudStorageDays: getDisplayedCloudDays(parseInt(previous_cloud_days)),
                accountLevel: accountlevel,
                cloudStorageDays: getDisplayedCloudDays(parseInt(cloud_days)),
                previousMaxDevices: previous_maxdevices,
                maxDevices: maxdevices,
                previousDevicesQuota: previous_devices_quota,
                devicesQuota: devices_quota,
                previousAnalyticsQuota: previous_analytics_devices,
                analyticsQuota: analytics_devices,
                cancellationDate: account_change_date && new Date(account_change_date),
                previousAnnualPaymentFrequency: previous_sub_renewal_frequency_months && previous_sub_renewal_frequency_months === 12,
                annualPaymentFrequency: sub_renewal_frequency_months && sub_renewal_frequency_months === 12,
                ...log
            };
        }
    );
});

/* ---------------------------- Health dashboard ---------------------------- */

export const getPopulationHealthCheckStats = makePrivateRequestAsManager(async ({ makePrivateRequest }, production = true) => {
    const { data } = await makePrivateRequest(
        'auth',
        'GET',
        `/health/adapters/summary`,
        {
            params: {
                environment: production ? 'production' : 'test'
            }
        }
    );

    return data;
}, { resourceType: 's' });

export const getAdaptersFailingHealthCheck = makePrivateRequestAsManager(async ({ makePrivateRequest }, check, production = true) => {
    const { data } = await makePrivateRequest(
        'auth',
        'GET',
        `/health/adapters/${check}`,
        {
            params: {
                environment: production ? 'production' : 'test'
            }
        }
    );

    return data;
}, { resourceType: 's' });

/* -------------------------------------------------------- */

export const requestSampleLoggerEmails = makePrivateRequestAsManager(async ({ makePrivateRequest }, logger, email, uidd, lang) => await makePrivateRequest(
      logger,
      'GET',
      '/send-emails',
      {
          params: {
              email,
              uidd,
              lang
          }
      }
  )
);

/**
 * Get all installers data
 * @returns array of installer entries
 */
export const getAllInstallersData = makePrivateRequestAsManager(async ({ makePrivateRequest }) => {
    const { data } = await makePrivateRequest(
        'auth',
        'GET',
        '/allinstallers',
        {
            params: {
                managementScope: 'all',
                managementType: 's',
                manageruid: getUid(),
                token: getManagementToken()
            }
        }
    );

    return data?.result || [];
}, { resourceType: 's' });
